import type { Notification } from 'src/types/Notifications';

/**
 * Group recert_tracking notifications by property_id into a single display
 * notification per property (recert_tracking_group). Other notifications
 * are returned unchanged. Used so the UI shows one bulk row per property
 * with bulk icon and modal.
 */
export function groupRecertTrackingNotifications(
  notifications: Notification[]
): Notification[] {
  const recertType = 'recert_notices_action_required';
  const singleType = 'recert_tracking';

  const rest: Notification[] = [];
  const byProperty = new Map<
    number,
    { propertyName: string; deepLink: string; notifs: Notification[] }
  >();

  for (const n of notifications) {
    if (
      n.notification_type !== recertType ||
      n.related_objects?.type !== singleType
    ) {
      rest.push(n);
      continue;
    }
    const propertyId = n.related_objects?.property_id;
    if (propertyId == null) {
      rest.push(n);
      continue;
    }
    if (!byProperty.has(propertyId)) {
      byProperty.set(propertyId, {
        propertyName: n.related_objects?.property_name ?? '',
        deepLink: n.related_objects?.deep_link ?? '',
        notifs: [],
      });
    }
    const entry = byProperty.get(propertyId)!;
    entry.notifs.push(n);
  }

  const grouped: Notification[] = [];
  for (const [propertyId, { propertyName, deepLink, notifs }] of byProperty) {
    const first = notifs[0];
    const rawItems = notifs.map((n) => ({
      tracking_id: n.related_objects?.tracking_id,
      certification_name: n.related_objects?.certification_name ?? '',
      timeframe_label: n.related_objects?.timeframe_label ?? '',
    }));
    // One notification is created per tracking per opted-in user; show unique recert actions (trackings) to match Upcoming Recertifications.
    // When tracking_id is missing or 0, do not dedupe (keep all) to avoid collapsing valid items and losing data.
    const seenTrackingIds = new Set<number>();
    const items = rawItems.filter((item) => {
      const id = item.tracking_id;
      if (id === 0 || id == null) {
        return true;
      }
      if (seenTrackingIds.has(id)) {
        return false;
      }
      seenTrackingIds.add(id);
      return true;
    });
    const uniqueRecertCount = items.length;
    const allRead = notifs.every((n) => n.is_read);
    const latestCreated = notifs.reduce(
      (acc, n) => (n.created > acc ? n.created : acc),
      notifs[0].created
    );
    grouped.push({
      ...first,
      id: first.id,
      created: latestCreated,
      is_read: allRead,
      content: `${uniqueRecertCount} Upcoming Recertifications`,
      related_objects: {
        type: 'recert_tracking_group',
        property_id: propertyId,
        property_name: propertyName,
        deep_link: deepLink,
        items,
        _groupedIds: notifs.map((n) => n.id),
      },
    });
  }

  const combined = [...rest, ...grouped];
  combined.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
  return combined;
}
